import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Package, FileText, Upload, Trash2, Loader2, Pencil, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import { InlineItemCode } from "@/components/ItemCode";
import { resolveImageUrl } from "@/lib/imageUrl";
import { formatDMY } from "@/lib/date";

const PLACEHOLDER = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZXF1aXBtZW50fGVufDB8fHx8MTc4NzEyMjgwMHww&ixlib=rb-4.1.0&q=85";

export default function CatalogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [m, setM] = useState(null);
  const [stock, setStock] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/catalog/${id}`), api.get(`/inventory?catalog_id=${id}`)]);
      setM(a.data);
      setStock(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are accepted"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("PDF exceeds 20 MB limit"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/catalog/${id}/spec-sheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setM(res.data);
      toast.success("Spec sheet uploaded");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeSpec = async () => {
    if (!window.confirm("Remove the uploaded spec sheet?")) return;
    try {
      const res = await api.delete(`/catalog/${id}/spec-sheet`);
      setM(res.data);
      toast.success("Spec sheet removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSpec = async () => {
    try {
      const res = await api.get(`/catalog/${id}/spec-sheet`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      // let browser hold the blob URL; revoke after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteModel = async () => {
    setDeleting(true);
    try {
      await api.delete(`/catalog/${id}`);
      toast.success(`"${m.model_name}" archived. Restore it anytime from the Audit Log.`);
      setConfirmOpen(false);
      navigate("/catalog");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      model_name: m.model_name || "",
      item_code: m.item_code || "",
      manufacturer: m.manufacturer || "",
      category: m.category || "",
      list_price: m.list_price ?? "",
      cost_price: m.cost_price ?? "",
      image_url: m.image_url || "",
      amc_interval_days: m.amc_interval_days ?? 90,
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const payload = {
        model_name: editForm.model_name.trim(),
        manufacturer: editForm.manufacturer.trim(),
        category: editForm.category.trim(),
        list_price: parseFloat(editForm.list_price) || 0,
        image_url: editForm.image_url.trim(),
        amc_interval_days: parseInt(editForm.amc_interval_days) || 90,
      };
      if (isAdmin) { payload.cost_price = parseFloat(editForm.cost_price) || 0; payload.item_code = editForm.item_code; }
      const res = await api.put(`/catalog/${id}`, payload);
      setM(res.data);
      toast.success("Model updated");
      setEditOpen(false);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSavingEdit(false);
    }
  };

  const onImageChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (JPG, PNG, WEBP)"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5 MB limit"); return; }
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/catalog/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setM(res.data);
      toast.success("Machine image updated");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setImgUploading(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  if (!m) return <div className="text-slate-500">Loading...</div>;

  const hasFile = !!m.spec_sheet_file;

  return (
    <div data-testid="catalog-detail-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/catalog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-catalog">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </Link>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={openEdit}
            data-testid="catalog-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit model
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              data-testid="catalog-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete model
            </Button>
          )}
        </div>
      </div>

      <ConfirmDelete
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={deleteModel}
        title="Delete this machine model?"
        recordLabel={m.model_name}
        softDelete
        loading={deleting}
        testIdPrefix="catalog-confirm-delete"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="aspect-square bg-slate-100 relative group">
              <img src={resolveImageUrl(m.image_url) || PLACEHOLDER} alt={m.model_name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => imgRef.current?.click()}
                disabled={imgUploading}
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/0 hover:bg-black/50 text-white opacity-0 hover:opacity-100 transition-opacity duration-150"
                data-testid="catalog-image-upload-btn"
                aria-label="Change image"
              >
                {imgUploading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-sm font-medium">Uploading…</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6" />
                    <span className="text-sm font-medium">Click to change image</span>
                    <span className="text-[10px] opacity-80">JPG · PNG · WEBP · up to 20 MB</span>
                  </>
                )}
              </button>
              <input
                ref={imgRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                capture="environment"
                onChange={onImageChosen}
                className="hidden"
                data-testid="catalog-image-file-input"
              />
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mb-1">{m.category}</div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900" data-testid="catalog-detail-name">{m.model_name}</h1>
            <div className="mt-2"><InlineItemCode model={m} canEdit={isAdmin} onSaved={(d) => setM((p) => ({ ...p, item_code: d.item_code }))} /></div>
            <div className="text-slate-500 mt-1">{m.manufacturer}</div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">List price</div>
                <div className="font-mono text-lg text-slate-900">₹ {Number(m.list_price).toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">AMC interval</div>
                <div className="text-lg text-slate-900">{m.amc_interval_days} days</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Units in stock</div>
                <div className="text-lg text-slate-900">{stock.filter((s) => s.status === "In Stock").length}</div>
              </div>
              {isAdmin && m.cost_price != null && Number(m.cost_price) > 0 && (
                <div className="col-span-2 md:col-span-3 pt-3 border-t border-dashed border-rose-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-rose-600 mb-1">Cost price · Admin only</div>
                      <div className="text-xs text-slate-500">Internal landed cost, hidden from Sales Reps and Managers</div>
                    </div>
                    <div className="font-mono text-xl text-rose-700" data-testid="catalog-cost-price">
                      ₹ {Number(m.cost_price).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Spec sheet</div>
              {hasFile ? (
                <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-md bg-slate-50">
                  <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate" data-testid="spec-file-name">
                      {m.spec_sheet_file.original_filename}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(m.spec_sheet_file.size / 1024).toFixed(0)} KB · uploaded {formatDMY(m.spec_sheet_file.uploaded_at)}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={openSpec} data-testid="open-spec-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="replace-spec-btn">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Replace
                  </Button>
                  <Button variant="outline" size="sm" onClick={removeSpec} data-testid="remove-spec-btn" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : m.spec_sheet_url ? (
                <div className="flex items-center gap-2">
                  <a href={m.spec_sheet_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-sm inline-flex items-center gap-1" data-testid="spec-sheet-link">
                    View legacy spec sheet URL <ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-xs text-slate-400">·</span>
                  <button onClick={() => fileRef.current?.click()} className="text-xs text-indigo-600 hover:underline" data-testid="upload-pdf-btn">Upload PDF instead</button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-md p-6 text-center">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-3">No spec sheet uploaded yet.</p>
                  <Button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="upload-pdf-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                    {uploading ? "Uploading..." : "Upload PDF"}
                  </Button>
                  <div className="text-xs text-slate-400 mt-2">PDF only · max 20 MB</div>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                onChange={onFileChosen}
                className="hidden"
                data-testid="spec-file-input"
              />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6 mt-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Stock units of this model</h2>
            {stock.length === 0 ? (
              <div className="text-slate-400 text-sm py-6 text-center">
                <Package className="w-6 h-6 mx-auto mb-1 text-slate-300" />
                No physical units logged. Add stock in Inventory.
              </div>
            ) : (
              <div className="space-y-2">
                {stock.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2">
                    <div className="font-mono text-sm text-slate-900">{s.serial_number}</div>
                    <div className="text-xs text-slate-500">{s.location}</div>
                    <Badge variant="secondary" className="text-xs">{s.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit model dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-edit-dialog">
          <DialogHeader><DialogTitle>Edit machine model</DialogTitle></DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model name *</Label>
                  <Input required value={editForm.model_name} onChange={(e) => setEditForm({ ...editForm, model_name: e.target.value })} data-testid="edit-model-input" className="mt-1" />
                </div>
                {isAdmin && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Item code</Label>
                    <Input value={editForm.item_code} onChange={(e) => setEditForm({ ...editForm, item_code: e.target.value.toUpperCase() })} placeholder="e.g. HFT-R5-STD" data-testid="edit-item-code-input" className="mt-1 font-mono uppercase placeholder:normal-case" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manufacturer *</Label>
                  <Input required value={editForm.manufacturer} onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })} data-testid="edit-mfr-input" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category *</Label>
                  <Input required value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid="edit-category-input" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">List price (INR) *</Label>
                  <Input required type="number" step="0.01" value={editForm.list_price} onChange={(e) => setEditForm({ ...editForm, list_price: e.target.value })} data-testid="edit-price-input" className="mt-1" />
                </div>
                {isAdmin && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-rose-600">Cost price (INR) · Admin only</Label>
                    <Input type="number" step="0.01" value={editForm.cost_price} onChange={(e) => setEditForm({ ...editForm, cost_price: e.target.value })} data-testid="edit-cost-price-input" className="mt-1 border-rose-200 focus:border-rose-400" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">AMC interval (days)</Label>
                  <Input type="number" value={editForm.amc_interval_days} onChange={(e) => setEditForm({ ...editForm, amc_interval_days: e.target.value })} data-testid="edit-amc-input" className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Image URL</Label>
                  <Input value={editForm.image_url} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })} data-testid="edit-image-url-input" className="mt-1" placeholder="https://... or upload from the image tile" />
                  <p className="text-[11px] text-slate-400 mt-1">Tip: to upload from your device, close this dialog and click the machine image on the left.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={savingEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-save-btn">
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
